
import React, { useEffect } from 'react';
import { AiSidebar } from './AiSidebar';
import { AiTopbar } from './AiTopbar';
import { AiAssistantProvider } from '@/contexts/AiAssistantContext';
import { SidebarProvider } from '@/contexts/SidebarContext';
import { cn } from '@/lib/utils';
import { ParticleBackground } from '../effects/ParticleBackground';
import { useAssistantMigration } from '@/hooks/use-assistant-migration';
import { Loader2 } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useAiAssistantSettings } from '@/hooks/use-ai-assistant-settings';
import { useIsMobile } from '@/hooks/use-mobile';
import ErrorBoundary from '../ErrorBoundary';
import { logger } from '@/utils/production-logger';

interface AiAssistantLayoutProps {
  children: React.ReactNode;
}

export const AiAssistantLayout = ({ children }: AiAssistantLayoutProps) => {
  const { isMigrating } = useAssistantMigration();
  const { user } = useAuth();
  const { ensureUserProfile } = useAiAssistantSettings();
  const isMobile = useIsMobile();
  
  // Force collapse sidebar by default on AI Assistant page
  const initialCollapsed = true;
  
  // Ensure user profile exists when the layout mounts
  useEffect(() => {
    if (user?.id) {
      const profileStart = performance.now();
      
      ensureUserProfile()
        .then(() => {
          const duration = performance.now() - profileStart;
          logger.debug('User profile ensured', {
            userId: user.id,
            duration,
            component: 'AiAssistantLayout'
          });
        })
        .catch(error => {
          logger.error('Error ensuring user profile exists in layout', {
            error: error instanceof Error ? error.message : String(error),
            userId: user.id,
            component: 'AiAssistantLayout'
          });
        });
    }
  }, [user?.id, ensureUserProfile]);
  
  const handleLayoutError = (error: Error, errorInfo: any) => {
    logger.error('Layout error boundary triggered', {
      error: error.message,
      stack: error.stack,
      componentStack: errorInfo.componentStack,
      userId: user?.id,
      component: 'AiAssistantLayout'
    });
  };
  
  if (isMigrating) {
    return (
      <div className="flex h-screen w-full bg-background text-foreground items-center justify-center">
        <div className="flex flex-col items-center space-y-4">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <p>Updating AI Assistant data...</p>
        </div>
      </div>
    );
  }
  
  return (
    <ErrorBoundary onError={handleLayoutError}>
      <SidebarProvider defaultCollapsed={initialCollapsed}>
        <AiAssistantProvider>
          <div 
            className={cn(
              "flex w-full bg-background text-foreground overflow-hidden",
              // Mobile-specific optimizations with proper viewport and touch handling
              isMobile && "touch-pan-y overscroll-contain"
            )}
            style={{
              // Use dynamic viewport height for better mobile support
              height: '100dvh',
              // Ensure proper stacking context
              position: 'relative',
              zIndex: 0,
              // CSS custom properties for safe area
              '--safe-area-inset-top': 'env(safe-area-inset-top)',
              '--safe-area-inset-bottom': 'env(safe-area-inset-bottom)',
            } as React.CSSProperties}
          >
            <ParticleBackground />
            <div 
              className="flex flex-1 relative"
              style={{
                // Ensure content stays above background
                zIndex: 1,
              }}
            >
              <ErrorBoundary>
                <AiSidebar />
              </ErrorBoundary>
              {/* Chat area wrapper with relative positioning for sticky elements */}
              <div className="flex flex-col flex-1 relative overflow-hidden">
                <ErrorBoundary>
                  <AiTopbar />
                </ErrorBoundary>
                <main 
                  className={cn(
                    "flex-1 overflow-hidden relative",
                    // Mobile-specific touch handling
                    isMobile && "touch-pan-y overscroll-contain"
                  )}
                  style={{
                    // Proper z-index for interactive content
                    position: 'relative',
                    zIndex: 2,
                  }}
                >
                  <ErrorBoundary>
                    {children}
                  </ErrorBoundary>
                </main>
              </div>
            </div>
          </div>
        </AiAssistantProvider>
      </SidebarProvider>
    </ErrorBoundary>
  );
};
